// proposal-red-anchor-check.ts
//
// Orchestrator recovery (3.40.0, D3.4): replacing a GREENING source
// (`implement-v1`/`fix-v1`, by controller-assigned `command_adapter`) that
// closes the RED window of testcase X (source ∈ green(X),
// scheduler-red-window-rule.ts) must keep the window anchored: after the
// batch, green(X) holds EXACTLY ONE greening created by this batch, and no
// ticket created by this batch stands before it — between X and the new
// greening only EXISTING tickets (a `test-v1` the source already waited on)
// may sit; extra split fragments come after it. Otherwise every green(X) is
// retired ⇒ an orphan RED window (open forever) ⇒ `red_anchor_lost`.
// Same scope as dangling rules (b)/(c): trigger `counted_cap|timeout_split`
// AND the batch supersedes the ledger's source ticket.
import { RETIRED_TICKET_STATUSES } from "./g2-plan-readiness.js";
import type {
  RecoveryBatchCheckInput,
  RecoveryBatchRejection,
} from "./proposal-dangling-dependency-check.js";
import { recoveryBatchScope } from "./proposal-dangling-dependency-check.js";
import {
  buildRedWindowGraph,
  GREENING_ADAPTER_IDS,
  TESTCASE_ADAPTER_IDS,
} from "./scheduler-red-window-rule.js";
import { ancestorsOf } from "./ticket-dag-reachability.js";

const reject = (reason: string): RecoveryBatchRejection => ({
  reason: `red_anchor_lost: ${reason}`,
  code: "red_anchor_lost",
});

/** D3.4: refusal for a recovery batch that orphans a RED window, else undefined. */
export function redAnchorRejection(
  input: RecoveryBatchCheckInput,
): RecoveryBatchRejection | undefined {
  const scope = recoveryBatchScope(input);
  if (scope === undefined || !scope.replacesSource) return undefined;
  const source = input.before.find((t) => t.id === scope.sourceTicketId);
  if (source === undefined || !GREENING_ADAPTER_IDS.includes(source.command_adapter)) {
    return undefined;
  }
  // green() walks `depends_on` edges only — status plays no part in it.
  const prior = buildRedWindowGraph(input.before, {});
  // A testcase this batch retires too has no window left to anchor (review G3 L6).
  const anchors = input.before.filter(
    (t) =>
      TESTCASE_ADAPTER_IDS.includes(t.command_adapter) &&
      prior.green(t.id).includes(source.id) &&
      !RETIRED_TICKET_STATUSES.has(input.statusAfter[t.id] ?? "todo"),
  );
  if (anchors.length === 0) return undefined;

  const createdIds = new Set(input.createdIds);
  const after = buildRedWindowGraph(input.after, input.statusAfter);
  const afterById = new Map(input.after.map((t) => [t.id, t]));
  for (const x of anchors) {
    const fresh = after.green(x.id).filter((id) => createdIds.has(id));
    const greening = fresh[0];
    if (fresh.length !== 1 || greening === undefined) {
      return reject(
        `testcase ${x.id} cần đúng 1 ticket implement/fix mới thay ${source.id} nối sau nó (đang có ${fresh.length})`,
      );
    }
    const newUpstream = [...ancestorsOf(greening, afterById)].find((id) => createdIds.has(id));
    if (newUpstream !== undefined) {
      return reject(
        `ticket mới ${newUpstream} đứng trước ${greening} — giữa ${x.id} và ticket implement/fix mới chỉ được có ticket sẵn có; mảnh chia thêm phải đứng sau ${greening}`,
      );
    }
  }
  return undefined;
}
